import { useRef, useEffect } from 'react'
import { useModalContext } from '@/context/ModalContext'
import ArticleSplitHero from '@/components/Article/ArticleSplitHero'
import ArticleMain from '@/components/Article/ArticleMain'
import ContentSections from '@/components/Sections/ContentSections'
import PageSEO from '@/components/SEO/PageSEO'
import StructuredData from '@/components/SEO/StructuredData'
import { useCustomerContext } from '@/context/CustomerContext'
import {
  getContentGatingModal,
  userCanAccessContent,
} from '@/utils/gatingUtils'
import { getGatedArticleModal } from '@/utils/getGatedArticleModal'
import { getArticlesByHandle } from '@/utils/getArticles'
import { getProductsByHandles } from '@/utils/getProducts'
import { getSanityClient } from '@/services/sanityClient'
import groq from 'groq'
import { getBlogSettings } from '@/utils/getBlogs'
import { PreviewButton } from '@/components/Preview/PreviewButton'
import { DateTime } from 'luxon'
import { ArticleJsonLd, VideoJsonLd } from 'next-seo'
import { BRAND_NAME } from '@/utils/constants'

const RecipeArticle = ({
  page,
  products,
  blogSettings,
  modals,
  preview,
  previewToken,
}) => {
  const { setContent } = useModalContext()
  const mainContentRef = useRef()

  const modalContext = useModalContext()
  const customerContext = useCustomerContext()
  const { customer } = customerContext

  const articleUrl = `${process.env.NEXT_PUBLIC_DOMAIN}/blogs/culinary/${page.fields.blog.handle.current}/${page.handle.current}`

  const datePublished = DateTime.fromISO(page.fields?.publishedDate || page.createdAt).toISO()
  const dateModified = DateTime.fromISO(page.updatedAt).toISO()

  const { hero, articleTags, displayTags } = page.fields
  const contentTags = articleTags?.map((t) => t.value)
  if (displayTags) {
    hero.tags = displayTags
  }
  const blogType = page.fields.blog?.blogType
  const blogGlobalSettings = blogSettings
    ? { ...blogSettings.fields[blogType], blogType }
    : undefined
  hero.header = page.title
  hero.subheader = page.fields.subheader

  // GATING
  useEffect(() => {
    if (page.type === 'liveCookingClassArticle') {
      // why is this in this use effect ?? -- ZJ
      setContent({
        header: page.title,
        classStartDate: page.fields.classStartDate,
        classEndDate: page.fields.classEndDate,
        listId: page.fields.klaviyoListId,
      })
    }

    const customerHasAccess = userCanAccessContent({
      contentTags,
      userTags: customer?.tags,
    })

    if (!customerHasAccess) {
      const foundModal = getContentGatingModal({ modals, contentTags })
      if (foundModal) {
        modalContext.setContent(foundModal.fields)
        modalContext.setModalType('gated_product')
        modalContext.setIsOpen(true)
      }
    }

    if (modalContext.modalType === 'gated_product' && customerHasAccess) {
      modalContext.setIsOpen(false)
    }
  }, [customer, modalContext.isOpen])

  if (page.type === 'recipeArticle') {
    return (
      <>
        {preview && previewToken && <PreviewButton />}
        <StructuredData type="article" data={page} />
        <StructuredData type="recipe" data={page} />
        <PageSEO
          seo={page.fields.seo}
          images={[{
            url: page.fields?.hero?.desktopBackgroundImage?.asset?.url,
            alt: page.fields?.hero?.header || page.title || ''
          }].filter(image => image.url)}
        />
        <ArticleJsonLd
          type="Recipe"
          url={articleUrl}
          title={page.fields?.seo?.metaTitle || page.title}
          images={[page.fields?.hero?.desktopBackgroundImage?.asset?.url].filter(Boolean)}
          description={page.fields?.seo?.metaDesc || ''}
          datePublished={datePublished}
          dateModified={dateModified}
          authorName={page.fields?.sidebar?.author?.name || BRAND_NAME}
          publisherName={BRAND_NAME}
          publisherLogo={blogSettings?.logo?.asset?.url || ''}
          isAccessibleForFree={true}
        />
        <ArticleSplitHero
          fields={hero}
          renderType="recipe"
          blogGlobalSettings={blogGlobalSettings}
        />
        <ArticleMain
          contentType="recipe"
          showNav={true}
          fields={page.fields}
          datePublished={datePublished}
          products={products}
          blogGlobalSettings={blogGlobalSettings}
        />
        <ContentSections sections={page.fields.pageContent} />
      </>
    )
  }

  if (page.type === 'videoArticle') {
    return (
      <>
        {preview && previewToken && <PreviewButton />}
        <StructuredData type="article" data={page} />
        <PageSEO
          seo={page.fields.seo}
          images={[{
            url: page.fields?.hero?.desktopBackgroundImage?.asset?.url,
            alt: page.fields?.hero?.header || page.title || ''
          }].filter(image => image.url)}
        />
        <ArticleJsonLd
          type="Article"
          url={articleUrl}
          title={page.fields?.seo?.metaTitle || page.title}
          images={[page.fields?.hero?.desktopBackgroundImage?.asset?.url].filter(Boolean)}
          description={page.fields?.seo?.metaDesc || ''}
          datePublished={datePublished}
          dateModified={dateModified}
          authorName={page.fields?.sidebar?.author?.name || BRAND_NAME}
          publisherName={BRAND_NAME}
          publisherLogo={blogSettings?.logo?.asset?.url || ''}
          isAccessibleForFree={true}
        />
        <ArticleSplitHero
          fields={hero}
          renderType="default"
          blogGlobalSettings={blogGlobalSettings}
        />
        <ArticleMain
          contentType="standard"
          fields={page.fields}
          datePublished={datePublished}
          products={products}
          blogGlobalSettings={blogGlobalSettings}
        />
        <ContentSections sections={page.fields.pageContent} />
      </>
    )
  }

  if (page.type === 'cookingGuideArticle') {
    const hasVideo = hero.youtubeVideoId ? true : false

    return (
      <div
        className={`${!hasVideo ? 'article-cooking-guide--no-video' : 'article-cooking-guide'}`}
      >
        {preview && previewToken && <PreviewButton />}
        <StructuredData type="article" data={page} />
        <PageSEO
          seo={page.fields.seo}
          images={[{
            url: page.fields?.hero?.desktopBackgroundImage?.asset?.url,
            alt: page.fields?.hero?.header || page.title || ''
          }].filter(image => image.url)}
        />
        <ArticleJsonLd
          type="Article"
          url={articleUrl}
          title={page.fields?.seo?.metaTitle || page.title}
          images={[page.fields?.hero?.desktopBackgroundImage?.asset?.url].filter(Boolean)}
          description={page.fields?.seo?.metaDesc || ''}
          datePublished={datePublished}
          dateModified={dateModified}
          authorName={page.fields?.sidebar?.author?.name || BRAND_NAME}
          publisherName={BRAND_NAME}
          publisherLogo={blogSettings?.logo?.asset?.url || ''}
          isAccessibleForFree={true}
        />
        {hasVideo && (
          <VideoJsonLd
            name={page.fields?.seo?.metaTitle || page.title}
            description={page.fields?.seo?.metaDesc || ''}
            thumbnailUrls={[page.fields?.hero?.desktopBackgroundImage?.asset?.url].filter(Boolean)}
            uploadDate={datePublished}
            contentUrl={`https://www.youtube.com/watch?v=${page.fields.hero.youtubeVideoId}`}
            embedUrl={`https://www.youtube.com/embed/${page.fields.hero.youtubeVideoId}`}
          />
        )}
        <ArticleSplitHero
          ref={mainContentRef}
          fields={hero}
          renderType="cooking-guide"
          blogGlobalSettings={blogGlobalSettings}
        />
        <ArticleMain
          ref={mainContentRef}
          contentType="standard"
          fields={page.fields}
          datePublished={datePublished}
          products={products}
          showNav={true}
          blogGlobalSettings={blogGlobalSettings}
        />
        <ContentSections sections={page.fields.pageContent} />
      </div>
    )
  }

  if (page.type === 'liveCookingClassArticle') {
    hero.classStartDate = page.fields?.classStartDate
    hero.classEndDate = page.fields?.classEndDate

    if (page.fields?.sidebar?.classSignup && page.fields.klaviyoListId) {
      page.fields.sidebar.classSignup.klaviyoListId = page.fields.klaviyoListId
    }

    return (
      <>
        {preview && previewToken && <PreviewButton />}
        <StructuredData type="article" data={page} />
        <PageSEO
          seo={page.fields.seo}
          images={[{
            url: page.fields?.hero?.desktopBackgroundImage?.asset?.url,
            alt: page.fields?.hero?.header || page.title || ''
          }].filter(image => image.url)}
        />
        <ArticleJsonLd
          type="Article"
          url={articleUrl}
          title={page.fields?.seo?.metaTitle || page.title}
          images={[page.fields?.hero?.desktopBackgroundImage?.asset?.url].filter(Boolean)}
          description={page.fields?.seo?.metaDesc || ''}
          datePublished={datePublished}
          dateModified={dateModified}
          authorName={page.fields?.sidebar?.author?.name || BRAND_NAME}
          publisherName={BRAND_NAME}
          publisherLogo={blogSettings?.logo?.asset?.url || ''}
          isAccessibleForFree={true}
        />
        <ArticleSplitHero
          fields={hero}
          renderType="cooking-class"
          blogGlobalSettings={blogGlobalSettings}
        />
        <ArticleMain
          contentType="standard"
          fields={page.fields}
          datePublished={datePublished}
          products={products}
          blogGlobalSettings={blogGlobalSettings}
        />
        <ContentSections sections={page.fields.pageContent} />
      </>
    )
  }

  // else return standard article
  return (
    <>
      {preview && previewToken && <PreviewButton />}
      <StructuredData type="article" data={page} />
      <PageSEO
        seo={page.fields.seo}
        images={[{
          url: page.fields?.hero?.desktopBackgroundImage?.asset?.url,
          alt: page.fields?.hero?.header || page.title || ''
        }].filter(image => image.url)}
      />
      <ArticleJsonLd
        type="Article"
        url={articleUrl}
        title={page.fields?.seo?.metaTitle || page.title}
        images={[page.fields?.hero?.desktopBackgroundImage?.asset?.url].filter(Boolean)}
        description={page.fields?.seo?.metaDesc || ''}
        datePublished={datePublished}
        dateModified={dateModified}
        authorName={page.fields?.sidebar?.author?.name || BRAND_NAME}
        publisherName={BRAND_NAME}
        publisherLogo={blogSettings?.logo?.asset?.url || ''}
        isAccessibleForFree={true}
      />
      <ArticleSplitHero
        fields={hero}
        renderType="default"
        blogGlobalSettings={blogGlobalSettings}
      />
      <ArticleMain
        contentType="standard"
        fields={page.fields}
        datePublished={datePublished}
        products={products}
        blogGlobalSettings={blogGlobalSettings}
      />
      <ContentSections sections={page.fields.pageContent} />
    </>
  )
}

export default RecipeArticle

export async function getStaticPaths() {
  const articles = await getSanityClient().fetch(
    groq`*[_type in ["standardArticle", "recipeArticle", "cookingGuideArticle", "liveCookingClassArticle", "videoArticle"]]{ handle { current }, blog->{ blogType, handle { current } } }`,
  )

  const handles = articles
    .filter((article) => article.blog.blogType === 'culinary')
    .map((article) => {
      return {
        params: {
          handle: article.handle.current,
          category: article.blog.handle.current,
        },
      }
    })

  return {
    paths: handles,
    fallback: 'blocking',
  }
}

export async function getStaticProps({ params, draftMode }) {
  const { category, handle } = params
  const preview = draftMode || false
  const previewToken = preview ? process.env.SANITY_READ_TOKEN : ``
  if (preview && !previewToken) {
    throw new Error(
      `Preview mode is active, but SANITY_READ_TOKEN is not set in environment variables`,
    )
  }

  const sanityClient = getSanityClient(previewToken)

  const pages = await getArticlesByHandle([handle], sanityClient)

  if (!pages.length || !pages[0].fields.published) {
    return {
      notFound: true,
    }
  }

  const validPage = pages.find((page) => {
    return page.fields?.blog?.handle.current === category
  })

  if (!validPage) {
    return {
      notFound: true,
    }
  }

  const [gatedArticleModal, blogSettings] = await Promise.all([
    getGatedArticleModal(sanityClient),
    getBlogSettings(sanityClient),
  ])

  const props = {
    page: validPage,
    handle: validPage.handle,
    blogSettings,
    modals: gatedArticleModal,
    preview,
    previewToken,
  }

  if (validPage.fields?.content) {
    const handles = validPage.fields.content
      .filter((item) => item._type === 'productBlock')
      .map((item) => item.product)
    if (handles.length) {
      const products = await getProductsByHandles({ productHandles: handles })
      if (products && products.length) {
        props.products = products
      }
    }
  }

  return {
    props,
  }
}
